import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { errorMessage } from '../api/client';
import { signupApi } from '../api/signup';
import { useSession } from '../auth/session';
import { Field, FormError, useSubmit } from '../components/Field';
import { AuthCard } from '../components/Layout';

function Consent({ checked, onChange, error, children }: { checked: boolean; onChange: (v: boolean) => void; error: string | undefined; children: ReactNode }) {
  return (
    <div className="field">
      <label className="checkbox"><input type="checkbox" checked={checked} onChange={(e) => { onChange(e.target.checked); }} /> <span>{children}</span></label>
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

function SignupForm({ formToken, previewMb }: { formToken: string; previewMb: number }) {
  const { signedIn } = useSession();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [adult, setAdult] = useState(false);
  const [terms, setTerms] = useState(false);
  const [website, setWebsite] = useState('');
  // `as true`: the server refuses false with a field message; the form sends what was ticked.
  const form = useSubmit(() => signupApi.signup({ username, displayName, password, adult: adult as true, terms: terms as true, formToken, website }), ({ user }) => {
    signedIn(user);
    void navigate('/', { replace: true });
  });
  const errors = form.fieldErrors;
  return (
    <form onSubmit={form.submit} noValidate>
      <Field label="Username" name="username" value={username} onChange={setUsername} autoComplete="username" error={errors.username} hint="3–32 characters: letters, digits, dot, dash, underscore." />
      <Field label="Display name" name="displayName" value={displayName} onChange={setDisplayName} autoComplete="name" error={errors.displayName} />
      <Field label="Password" name="password" type="password" value={password} onChange={setPassword} autoComplete="new-password" error={errors.password} hint="At least 10 characters." />
      {/* People never see this field; bots fill it in (doc 64). */}
      <div className="trap" aria-hidden="true">
        <label htmlFor="signup-website">Website</label>
        <input id="signup-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => { setWebsite(e.target.value); }} />
      </div>
      <Consent checked={adult} onChange={setAdult} error={errors.adult}>I am at least 18 years old.</Consent>
      <Consent checked={terms} onChange={setTerms} error={errors.terms}>
        I accept the <Link to="/legal/terms" target="_blank">terms of use</Link> and have read the <Link to="/legal/privacy" target="_blank">privacy policy</Link>.
      </Consent>
      <p className="muted">Your gallery keeps previews up to {previewMb} MB; the oldest are removed first. Full-size pictures stay on your machines — download the ones you want to keep.</p>
      <FormError message={form.formError} />
      <button type="submit" disabled={form.pending}>Create account</button>
    </form>
  );
}

/** Open sign-up (doc 64): an account without an invite, while the owner allows it. */
export function SignupPage() {
  const form = useQuery({ queryKey: ['signup-form'], queryFn: signupApi.form, retry: false, staleTime: 30 * 60_000 });
  return (
    <AuthCard title="Create your Couch account">
      {form.isPending && <p role="status">Loading…</p>}
      {form.isError && <p role="alert" className="form-error">{errorMessage(form.error)}</p>}
      {form.data && <SignupForm formToken={form.data.formToken} previewMb={form.data.previewMb} />}
      <p className="muted">Already have an account? <Link to="/login">Sign in</Link></p>
    </AuthCard>
  );
}
